<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { Check, ChevronDown, Search, X } from "@lucide/vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: "Secim yapin" },
  searchPlaceholder: { type: String, default: "Listede ara..." },
  searchable: { type: Boolean, default: false },
  clearable: Boolean,
  disabled: Boolean,
});
const emit = defineEmits(["update:modelValue", "change"]);
const root = ref(null);
const searchInput = ref(null);
const open = ref(false);
const query = ref("");

const normalized = computed(() =>
  props.options.map((option) =>
    typeof option === "object"
      ? option
      : { value: option, label: String(option) },
  ),
);
const selected = computed(() =>
  normalized.value.find(
    (option) => String(option.value) === String(props.modelValue),
  ),
);
const filtered = computed(() => {
  const needle = query.value.trim().toLocaleLowerCase("tr-TR");
  if (!needle) return normalized.value;
  return normalized.value.filter((option) =>
    `${option.label} ${option.description || ""}`
      .toLocaleLowerCase("tr-TR")
      .includes(needle),
  );
});

async function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value && props.searchable) {
    query.value = "";
    await nextTick();
    searchInput.value?.focus();
  }
}
function choose(option) {
  emit("update:modelValue", option.value);
  emit("change", option.value);
  open.value = false;
}
function clear(event) {
  event.stopPropagation();
  emit("update:modelValue", "");
  emit("change", "");
}
function outside(event) {
  if (!root.value?.contains(event.target)) open.value = false;
}
function keyboard(event) {
  if (event.key === "Escape") open.value = false;
}
onMounted(() => {
  document.addEventListener("mousedown", outside);
  document.addEventListener("keydown", keyboard);
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", outside);
  document.removeEventListener("keydown", keyboard);
});
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      :disabled="disabled"
      :class="[
        'ui-select-trigger',
        open && 'border-brand-500 ring-2 ring-brand-500/10',
      ]"
      @click="toggle"
    >
      <span :class="selected ? 'text-ink-900' : 'text-slate-400'" class="truncate">
        {{ selected?.label || placeholder }}
      </span>
      <span class="ml-auto flex shrink-0 items-center gap-1">
        <span
          v-if="clearable && selected"
          class="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          title="Secimi temizle"
          @click="clear"
        ><X class="size-3.5" /></span>
        <ChevronDown :class="['size-4 text-slate-400 transition-transform', open && 'rotate-180']" />
      </span>
    </button>
    <Transition name="select-pop">
      <div v-if="open" class="ui-select-menu">
        <div v-if="searchable" class="border-b border-slate-100 p-2">
          <div class="relative">
            <Search class="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
            <input
              ref="searchInput"
              v-model="query"
              class="input h-9 pl-9"
              :placeholder="searchPlaceholder"
              @keydown.stop
            />
          </div>
        </div>
        <div class="max-h-64 overflow-y-auto p-1.5">
          <button
            v-for="option in filtered"
            :key="String(option.value)"
            type="button"
            :class="[
              'ui-select-option',
              String(option.value) === String(modelValue) && 'bg-brand-50 text-brand-700',
            ]"
            @click="choose(option)"
          >
            <span class="min-w-0 flex-1 text-left">
              <strong class="block truncate text-sm font-semibold">{{ option.label }}</strong>
              <small v-if="option.description" class="mt-0.5 block truncate text-xs text-slate-400">{{ option.description }}</small>
            </span>
            <Check v-if="String(option.value) === String(modelValue)" class="size-4 shrink-0" />
          </button>
          <div v-if="!filtered.length" class="px-3 py-8 text-center text-sm text-slate-400">
            Eslesen kayit bulunamadi.
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
